import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { Gender } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import needle from 'needle';
import { Adresse, ZipCity } from '../adresse.js';
import { Mitarbeiter } from '../master-data-management/users/entities/Mitarbeiter.js';
import { SoftgardenImportRecord } from './SoftgardenImportRecord.js';

/**
 * This function returns one constructed Mitarbeiter build from Softgarden data. If the given key doesnt belong to a user it throws
 * @param key the unique identifier for the softgarden record the user has given
 */
export async function getMitarbeiterFromSoftgardenKeyFromCache(key: string) {
  let cacheRecord = await getRecordFromCacheWithKey(key);
  if (cacheRecord) {
    return constructMitarbeiterFromCacheRecord(cacheRecord);
  }

  const updatedWithNewValues = await updateCache();
  if (updatedWithNewValues) {
    cacheRecord = await getRecordFromCacheWithKey(key);
    if (cacheRecord) {
      return constructMitarbeiterFromCacheRecord(cacheRecord);
    }
  }

  throw new Error('Der Bewerber mit der Bewerbungs-ID"' + key + '" konnte nicht gefunden werden!');
}

async function getRecordFromCacheWithKey(key: string): Promise<Maybe<EntityData<SoftgardenImportRecord>>> {
  const cacheCollection = await getRawCollection<EntityData<SoftgardenImportRecord>>(
    SoftgardenImportRecord._collectionName,
  );
  const possibleCacheRecord = await cacheCollection.findOne({ key });
  return possibleCacheRecord;
}

async function constructMitarbeiterFromCacheRecord(record: EntityData<SoftgardenImportRecord>) {
  const sofgardenContent = JSON.parse(record.softgardenContent ?? '');
  const softgardenPersonData = JSON.parse(sofgardenContent.data).variables.input.employees[0];

  const genderMap = {
    male: Gender.MALE,
    female: Gender.FEMALE,
  };

  const constructedMitarbeiter = new Mitarbeiter({
    title: softgardenPersonData.titlePrefix.join(' ').trim(),
    geschlecht: genderMap[softgardenPersonData.gender] ?? Gender.DIVERSE,
    vorname: softgardenPersonData.firstName,
    nachname: softgardenPersonData.lastName,
    geburtstag: DateTime.fromFormat(softgardenPersonData.birthday, 'yyyy-LL-dd'),
    adresse: new Adresse({
      zipCity: new ZipCity({
        city: softgardenPersonData.city,
        zip: softgardenPersonData.postalCode,
      }),
      strasse: softgardenPersonData.streetName,
    }),
    email: softgardenPersonData.email,
    handynummer: softgardenPersonData.mobile[0] ?? softgardenPersonData.phone[0],
  });

  return constructedMitarbeiter;
}

/**
 *
 * @returns boolean whether the cache has been updated
 */
async function updateCache() {
  const endpoint = `https://app.softgarden.io/api/export/application/download?exportPublicId=${projectConfig.get(
    'SOFTGARDEN_PUBLIC_ID',
  )}`;

  // Call the softgarden endpoint
  const response = await needle('get', endpoint, {
    headers: { Authorization: `Bearer ` + projectConfig.get('SOFTGARDEN_TOKEN'), CacheControl: `no-store` },
  });

  // Check for correct response
  if (response.statusCode !== 200) {
    throw new Error(
      'Tried to fetch new data from Softgarden API but got status code: ' +
        response.statusCode +
        ' | 40* = Our problem, 50* = Their problem',
    );
  }

  const result = response.body;
  if (!Array.isArray(result)) {
    throw new Error(
      'Tried to fetch new data from Softgarden API but got no Array return! Do we call the correct Endpoint?',
    );
  }

  // No new values. We cant update the cache
  if (!result.length) {
    logger.info('[Softgarden Cache] No new values were returned from Softgarden');
    return false;
  }

  // Build new records
  const newCacheRecords: SoftgardenImportRecord[] = [];
  for (const responseRecord of result) {
    newCacheRecords.push(
      new SoftgardenImportRecord({
        key: responseRecord.applicationPublicId,
        softgardenContent: JSON.stringify(responseRecord),
      }),
    );
  }

  // insert records into cache
  const cacheCollection = await getRawCollection<EntityData<SoftgardenImportRecord>>(
    SoftgardenImportRecord._collectionName,
  );
  const writeResult = await cacheCollection.insertMany(newCacheRecords.map(record => record.toJSON()));

  logger.info('[Softgarden Cache] Updated the cache with ' + writeResult.insertedCount + ' new records');

  return true;
}
